// @flow
import * as React from 'react';
import { Box, Button } from 'grommet';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import sidePanelMessages from 'esm-web-pro/messages/sidePanelMessages';
import Point12Select from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/connect/Point12Select';
import PortSelect from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/connect/PortSelect';
import RangeSelect from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/connect/RangeSelect';
// import type { ODFPort as ODFPortType } from 'esm-web-pro/types/networks';
import type { Map } from 'esm-web-pro/containers/map/types';
import LengthInput from 'esm-web-pro/components/forms/fields/LengthInput';
import usePrevious from 'esm-web-pro/hooks/usePrevious';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import MapFiberConnection from 'esm-web-pro/containers/pages/network/objects/point12/MapFiberConnection';
import SFPSelect from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/connect/SFPSelect';


type Props = {
    // eslint-disable-next-line react/no-unused-prop-types
    container: Object,
    // eslint-disable-next-line react/no-unused-prop-types
    map: Map,
    getPoPLayers12: Function,
    onClose: Function,
    onSave: Function,
    port: Object,
    ports: {[number]: {
        fiber: ?Object, port: ?Object,
    }},
    connectPortsToPorts: Function,
    PoPLayers12: Object,
    editor: BaseEditor,
    intl: IntlShape,
};

const ConnectPortsToPorts = (props: Props) => {
    const {
        container: PopLayer12, getPoPLayers12, intl: { formatMessage }, onClose, onSave,
        PoPLayers12, port, ports, connectPortsToPorts, editor, map: networkMap,
    } = props;

    const [point, setPoint] = React.useState(null);
    const [secondPort, setSecondPort] = React.useState(null);
    const [sfp, setSfp] = React.useState(null);
    const [secondPoint14, setSecondPoint14] = React.useState(null);
    const [range, setRange] = React.useState(1);
    const [rangeError, setRangeError] = React.useState(null);
    const [length, setLength] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);

    const prevPoint = usePrevious(point);
    const prevSecondPort = usePrevious(secondPort);

    const getPorts = React.useCallback(() => {
        const pointObject = PoPLayers12[point];
        let portsAccessor = '';
        if (pointObject.type === 'odf') {
            portsAccessor = 'optical_odf_ports';
        } else if (pointObject.type === 'switch') {
            portsAccessor = 'optical_switch_ports';
        }
        let currentPorts = Object.values(pointObject[portsAccessor]);
        if (PoPLayers12[point].type === 'switch' && sfp) {
            currentPorts = currentPorts.filter((p) => p.sfp_port_number === sfp);
        }
        return currentPorts;
    }, [point]);

    const findSecondFiber = React.useCallback(() => {
        if (!secondPort || !point) {
            setSecondPoint14(null);
        } else if (prevSecondPort !== secondPort || prevPoint !== point) {
            const portObject = getPorts().find((p) => p.sequence === Number(secondPort));
            let resource = '';
            const pointObject = PoPLayers12[point];
            if (pointObject.type === 'odf') {
                resource = 'opticalodfport';
            } else if (pointObject.type === 'switch') {
                resource = 'opticalswitchport';
            }

            if (portObject && resource) {
                setSecondPoint14({ uuid: portObject.uuid, resource });
            }
        }
    }, [point, secondPort, PopLayer12]);

    React.useEffect(() => {
        findSecondFiber();
    }, [findSecondFiber]);

    React.useEffect(() => {
        getPoPLayers12();
    }, []);

    React.useEffect(() => {
        if (!point) {
            setSecondPort(null);
            setRange(1);
        }
    }, [point]);

    const getMaxPort = (initialPort, allPorts) => {
        let maxPort = initialPort;
        allPorts.forEach((p) => {
            if (p.sequence - maxPort === 1 && !p.port) {
                maxPort = p.sequence;
            }
        });

        return maxPort;
    };

    const maxRange = React.useCallback(() => {
        if (!point || !secondPort) return;
        let pointPorts = Object.values(ports);

        if (PopLayer12.type === 'switch' && sfp) {
            pointPorts = pointPorts.filter((p) => p.sfp_port_number === sfp);
        }
        const maxPort = getMaxPort(port.sequence, pointPorts);
        const maxPointPort = getMaxPort(secondPort, getPorts());

        // eslint-disable-next-line consistent-return
        return (maxPort && maxPointPort)
            ? Math.min(
                maxPort - port.sequence + 1,
                maxPointPort - secondPort + 1,
            ) : 0;
    }, [point, secondPort]);

    const handleClose = () => {
        onClose();
    };

    const handleSave = () => {
        setSubmitting(true);
        const data = {
            first_port: port.sequence,
            point_layer1_2: PoPLayers12[point].uuid,
            second_port: secondPort,
            range,
            cord_length: length,
            sfp_first_port_number: Object.prototype.hasOwnProperty.call(port, 'sfp_port_number')
                && port.sfp_port_number,
            sfp_second_port_number: Number(sfp),
        };
        connectPortsToPorts(PopLayer12.uuid, data).then(() => {
            setSubmitting(false);
            onSave();
        });
    };

    const handleChangePoint = (pointVal) => {
        setSecondPort(null);
        setSfp(null);
        setPoint(pointVal);
    };

    return (
        <>
            <Spinner loading={submitting} />
            <Box height="100%">
                <MapFiberConnection
                    point={PopLayer12.container}
                    initialFiber={port.fiber ? port.fiber.uuid : null}
                    editor={editor}
                    map={networkMap}
                    secondPoint14={secondPoint14}
                />
                <Box
                    direction="row"
                    align="center"
                    height="100%"
                    overflow="auto"
                    style={{ position: 'unset' }}
                >
                    <Box alignSelf="stretch" flex justify="between">
                        <Box flex={{ shrink: 0 }} pad={{ bottom: 'small' }}>
                            <Box height="12px" flex={{ shrink: 0 }} />
                            <Box pad={{ horizontal: 'medium' }}>
                                {PoPLayers12 ? (
                                    <Point12Select
                                        PoPLayers12={PoPLayers12}
                                        value={point}
                                        onChange={handleChangePoint}
                                    />
                                ) : (
                                    <Spinner
                                        type="clipLoader"
                                        loading
                                        blocking={false}
                                        color="black"
                                        coverStyle={{
                                            position: 'relative',
                                            width: 'auto',
                                            height: 'auto',
                                            top: '10px',
                                            padding: '30px',
                                        }}
                                    />
                                )}
                                <Box height="12px" flex={{ shrink: 0 }} />
                                {(point && PoPLayers12[point].type === 'switch') && (
                                    <SFPSelect
                                        PoPLayer12={PoPLayers12[point]}
                                        value={sfp}
                                        onChange={(val) => { setSecondPort(null); setSfp(val); }}
                                    />
                                )}
                                {point && (PoPLayers12[point].type !== 'switch' || sfp) && (
                                    <PortSelect
                                        PoPLayer12={PoPLayers12[point]}
                                        value={secondPort}
                                        onChange={setSecondPort}
                                        sfpNumber={sfp}
                                    />
                                )}
                                <Box height="12px" flex={{ shrink: 0 }} />
                                {(point && secondPort) && (
                                    <RangeSelect
                                        calculateMaxRange={maxRange}
                                        onChange={setRange}
                                        value={range}
                                        error={rangeError}
                                        onError={setRangeError}
                                    />
                                )}
                                <Box height="12px" flex={{ shrink: 0 }} />
                                {PoPLayers12 && (
                                    <LengthInput
                                        label={(
                                            <FormattedMessage
                                                id="patch-cord-length-title"
                                                defaultMessage="Patch cord length"
                                            />
                                        )}
                                        onChange={(e) => setLength(e.target.value)}
                                        value={length}
                                        unit={(
                                            <FormattedMessage
                                                id="meter-short"
                                                defaultMessage="m."
                                            />
                                        )}
                                        required
                                    />
                                )}
                            </Box>
                        </Box>
                        <ActionButtons
                            onClose={handleClose}
                            onSave={handleSave}
                            disabled={!point || !secondPort || !range || !length || rangeError}
                        />
                    </Box>
                </Box>
            </Box>
        </>
    );
};


const apiCallback = (apiClient: ApiClient, props: Props) => ({
    getPoPLayers12: {
        send: () => apiClient.resources[props.container.type].getODFsAndSwitches(props.container.uuid, {
            layer12__identity: props.container.identity,
            container__uuid: props.container.container.uuid,
            with_optical_ports: true,
            is_draft: props.map.uuid,
        }),
        success: (response: { results: Array<Object> }) => ({
            PoPLayers12: response.results,
        }),
    },
    connectPortsToPorts: {
        send: (uuid: string, data: Object) => apiClient.resources[props.container.type].connectPorts(uuid, data),
    },
});

export default injectIntl(connectAPI(apiCallback)(ConnectPortsToPorts));
